import api from 'services/api';
import { REJECT, array, num, object, refuseDegraded, str } from 'validation';
import { readResponse } from 'api/shared/readResponse';

// A PROJECTION CONTRACT over `GET /zones` — see ../../README.md. The full contract
// is `getZones`, which the zones list page needs; a zone dropdown reads two fields.
//
// Declaring only those is what keeps the dropdown working when something it never
// looks at drifts. Concretely: `getZones` records a legacy object-format
// `retention_policies` — a shape its own comment says exists in the wild — which
// degraded the whole page and emptied the Live Events zone filter. Nothing here
// parses that column, so nothing here can warn about it.

/** A zone as a dropdown deals in it. */
export interface ZoneSummary {
  readonly zone_id: number;
  readonly name: string;
}

const summarySchema = object<ZoneSummary>({
  zone_id: num(REJECT),
  name: str(),
});

interface ZonesLOVResponse {
  readonly zones: readonly ZoneSummary[];
}

const responseSchema = object<ZonesLOVResponse>({
  zones: array(summarySchema),
});

const LOV_LIMIT = 1000;

/**
 * Every zone the current user can see, narrowed to what a dropdown renders.
 *
 * Collapses with `refuseDegraded`, and that is a property of this contract rather
 * than a policy imposed on an unknown consumer: a zone missing from a filter is one
 * the operator cannot filter to, with nothing on screen admitting it. A partial list
 * of values is not a list of values.
 *
 * NOT the same question as `getAuthorizedZones`, which asks which zones the user may
 * perform a given action in. This one lists what they can see.
 */
export const getZonesLOV = async (): Promise<readonly ZoneSummary[]> => {
  const res = await api.get('/zones', { params: { limit: LOV_LIMIT } });
  return refuseDegraded(readResponse(res.data, { schema: responseSchema, source: 'getZonesLOV' })).zones;
};
